import { debounce } from 'debounce'
import templatesActions from 'ServicesActions/templates'
import { Notification } from 'Utils/Classes/Notification'
import { slugify, formatDate, sortArrayByObjectKey, checkApiResponse, setObjectKeys } from 'Utils'

// Setting up debounce
let syncTimeout = null

// Setting up notification
const notification = new Notification()
notification.timeout = 5000
notification.message =
  'Hubo un error al guardar los datos en la aplicación, pero se han guardado correctamente en el servidor'

export const namespaced = true

export const state = {
  templates: null,
  templateDraft: null,
  templatePublished: null,
  templateTranslation: null,
  syncStatus: 'modified',
}

export const mutations = {
  SET_TEMPLATE(state, template) {
    state.templates.push(template)
    state.templates = sortArrayByObjectKey(state.templates, 'modified', true)
  },

  SET_TEMPLATES(state, templates) {
    state.templates = sortArrayByObjectKey(templates, 'modified', true)
  },

  SET_TEMPLATE_UPDATE(state, tempalteData) {
    tempalteData.template[tempalteData.key] = tempalteData.newValue
  },

  SET_TEMPLATE_TRANSLATION(state, template) {
    state.templateTranslation = template
  },

  SET_PUBLISHED_TEMPLATE(state, template) {
    state.templatePublished = template
  },

  SET_TEMPLATE_DRAFT(state, templateDraft) {
    state.templateDraft = templateDraft
  },

  SET_TEMPLATE_DRAFT_UPDATE(state, templateData) {
    for (const key of templateData.updatedValues.keys) {
      templateData.templateDraft[key] = templateData.updatedValues[key]
    }
  },

  SET_SYNC_STATUS(state, status) {
    state.syncStatus = status
    clearTimeout(syncTimeout)
    syncTimeout = null
  },
}

export const actions = {
  async loadAll({ commit }) {
    const templates = await templatesActions.get.templates()
    // adding necessary info for listing page
    templates.data.forEach((t) => {
      t['slug'] = slugify(t.title)
      t['created_formated'] = formatDate(t['created'])
      t['modified_formated'] = formatDate(t['modified'])
    })
    commit('SET_TEMPLATES', templates.data)
  },

  async loadTemplateDraft({ commit }, tempId) {
    const templateDraft = await templatesActions.get.templateRecentDraft(tempId)
    if (!checkApiResponse(templateDraft)) return templateDraft
    commit('SET_TEMPLATE_DRAFT', templateDraft.data)
    return templateDraft.data
  },

  async loadPublishedTemplate({ commit }, tempId) {
    const template = await templatesActions.get.templatePublished(tempId)
    if (!checkApiResponse(template)) return template
    commit('SET_PUBLISHED_TEMPLATE', template.data)
    return template.data
  },

  async loadTemplateTranslation({ commit }, { id, language }) {
    const templateTranslations = await templatesActions.get.templateTranslations(id)

    if (!checkApiResponse(templateTranslations)) return templateTranslations

    const translation = templateTranslations.data.filter((t) => t.language === language)[0]
    commit('SET_TEMPLATE_TRANSLATION', translation)
    return templateTranslations
  },

  async addTemplate({ commit }, { title, category, language }) {
    try {
      const newTemplate = await templatesActions.add.template({ title, category, language })
      newTemplate.data = {
        ...newTemplate.data,
        slug: slugify(newTemplate.data.title),
        created_formated: formatDate(newTemplate.data.created),
        modified_formated: formatDate(newTemplate.data.modified),
      }

      commit('SET_TEMPLATE', newTemplate.data)
      return newTemplate
    } catch (error) {
      return error
    }
  },

  async addTemplateTranslation(context, { id, langCode }) {
    try {
      const newTanslation = await templatesActions.add.templateTranslation(id, {
        language: langCode,
      })

      return newTanslation
    } catch (error) {
      return error
    }
  },

  async updateTemplate(context, newValues) {
    try {
      const template = context.state.templatePublished || context.state.templateDraft

      const update = await templatesActions.update.template(template.card_id, newValues)
      const updatedValues = Object.keys(newValues)

      try {
        // draft updating in case of editors
        if (updatedValues[0].includes('editor')) {
          // setting up data for update
          const draftUpdating = {
            templateDraft: context.state.templateDraft,
            updatedValues: {
              keys: ['editors'],
              editors: update.data.editors,
            },
          }
          // we update the template draft
          context.commit('SET_TEMPLATE_DRAFT_UPDATE', draftUpdating)
        } else {
          // template updating in case of status draft
          // const today = new Date()
          const templateUpdating = {
            template,
            key: 'status',
            newValue: update.data.status,
          }

          context.commit('SET_TEMPLATE_UPDATE', templateUpdating)

          context.dispatch('loadPublishedTemplate', update.data.card_id)
          // updating modified time
          // context.dispatch('updateTemplateDraft', { modified: today })
        }
        return update
      } catch (error) {
        notification.dispatch()
        return update
      }
    } catch (error) {
      notification.message = `Hubo un error al actulizar: ${error.message}`
      notification.dispatch()
      return error
    }
  },

  async updateTemplateDraft(context, newValue) {
    context.commit('SET_SYNC_STATUS', 'saving')
    try {
      const templateDraft = context.state.templateDraft
      const update = await templatesActions.update.templateDraft(templateDraft.card_id, newValue)

      // adding modified date
      newValue.modified = update.data.modified
      // is a category
      if (newValue.category) newValue.category = update.data.category
      const updatedValues = setObjectKeys(newValue)
      // setting up data for update
      const templateData = {
        templateDraft,
        updatedValues,
      }

      context.dispatch('changeSyncStatus', 'synced')
      try {
        context.commit('SET_TEMPLATE_DRAFT_UPDATE', templateData)

        // context.commit('SET_TEMPLATE_DRAFT', update.data)
        return update
      } catch (error) {
        notification.dispatch()
        return update
      }
    } catch (error) {
      context.dispatch('changeSyncStatus', 'error')
      return error
    }
  },

  updateTemplateDraftTitle: debounce(function ({ dispatch }, values) {
    dispatch('updateTemplateDraft', values)
  }, 2000),

  updateTemplateDraftSlots: debounce(function ({ dispatch }, values) {
    if (!values) return
    dispatch('updateTemplateDraft', values)
  }, 2000),

  changeSyncStatus({ commit }, status) {
    commit('SET_SYNC_STATUS', status)
    syncTimeout = setTimeout(() => {
      commit('SET_SYNC_STATUS', 'modified')
    }, 3000)
  },
}

export const getters = {
  getTemplate: (state) => {
    return (id) => {
      const currentId = !id ? state.templateDraft.card_id : id

      return state.templates?.filter((tem) => tem.card_id == currentId)
    }
  },

  getTemplateTranslation: (state) => {
    return (id) => {
      return state.templates?.forEach((tem) => tem.translations.filter((t) => t.id == id))
    }
  },

  getTemplatesByUser: (state) => {
    return (id) => {
      return state.templates?.reduce((acc, template) => {
        if (template.editors.some((editor) => editor.id == id)) acc.push(template)
        return acc
      }, [])
    }
  },
}
